import { styled } from "simplydo/react/styled";
import { Dropdown } from "simplydo/react/ui/dropdown";
import { Input } from "simplydo/react/ui/input";

import type { UISize } from "../utils/UIContext";
import { SIZE_FONT } from "../utils/colors";
import { FormGroup } from "./FormGroup";

export type FieldStyleProps = { $widthPct?: string };

export const StyledField = styled.div<FieldStyleProps>`
  clear: both;

  ${Input}, ${Dropdown} {
    display: flex;
    width: 100%;
    min-width: 0;
  }

  &.is-width {
    width: ${({ $widthPct }) => $widthPct};
    display: inline-block;
    vertical-align: top;
  }

  &.is-disabled {
    opacity: 0.45;
    pointer-events: none;
  }

  &.is-inline {
    display: flex;
    align-items: center;
    gap: 0.5em;
    > label {
      margin: 0;
      width: auto;
    }
  }

  > label {
    display: block;
    margin: 0 0 0.5rem;
    color: rgba(0, 0, 0, 0.87);
    font-size: 1em;
    font-weight: 700;
    text-transform: none;
  }

  &.is-required > label::after {
    content: " *";
    color: #db2828;
  }

  &.is-error > label {
    color: #9f3a38;
  }

  &.is-error input,
  &.is-error textarea,
  &.is-error ${Dropdown} {
    background: #fff6f6 !important;
    border-color: #e0b4b4 !important;
    color: #9f3a38 !important;
  }
`;

export const StyledGroup = styled.div<GroupStyleProps>`
  display: flex;
  flex-wrap: wrap;
  flex: 1;
  align-items: flex-start;
  gap: 0.5em 1em;
  width: 100%;

  &.is-equal-width > ${StyledField} {
    flex: 1 1 0;
    min-width: 0;
    margin-bottom: 0;
  }

  &.is-grouped {
    flex-direction: column;
    gap: 0.5em;
  }

  &.is-disabled {
    opacity: 0.45;
    pointer-events: none;
  }

  &.is-error {
    color: #9f3a38;
  }
`;

export const StyledForm = styled.form<{ $size?: UISize }>`
  display: flex;
  flex-direction: column;
  position: relative;
  max-width: 100%;
  font-family: inherit;
  font-size: ${({ $size }) => SIZE_FONT[$size || "medium"]};
  gap: 0.5em 1em;
  flex-wrap: wrap;

  &.is-equal-width {
    ${FormGroup} > ${StyledField},
    > ${StyledField} {
      flex: 1 1 0;
      min-width: 0;
    }
  }

  &.is-loading::before {
    content: "";
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.8);
    z-index: 10;
  }

  &.is-loading::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 2em;
    height: 2em;
    margin: -1em 0 0 -1em;
    border: 0.2em solid rgba(0, 0, 0, 0.1);
    border-top-color: #767676;
    border-radius: 50%;
    animation: sdo-form-spin 0.6s linear infinite;
    z-index: 11;
  }

  @keyframes sdo-form-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export type GroupStyleProps = { $inline?: boolean };

export const ErrorLabel = styled.div`
  color: #9f3a38;
  margin-top: 0.3em;
  font-size: 0.875em;
`;

export function fieldWidthPct(width?: number | string): string | undefined {
  if (width == null) return undefined;
  const n = Number(width);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return `${(n / 16) * 100}%`;
}
